import { type Shift } from '@libraries/typescript/api_client';

type ShiftGroupForOrdering = {
    readonly shiftIds?: ReadonlyArray<string>;
    readonly shift_ids?: ReadonlyArray<string>;
};

type ShiftGroupForDisplayUnits = ShiftGroupForOrdering & {
    readonly id?: string;
    readonly groupMode?: 'all' | 'any';
    readonly group_mode?: 'all' | 'any';
};

type ShiftForOrdering = Pick<Shift, 'id' | 'shift_start_time'>;

export type OutreachDisplayUnit<T extends ShiftForOrdering> =
    | {
          readonly kind: 'shift';
          readonly id: string;
          readonly shiftIds: Array<string>;
          readonly shifts: Array<T>;
      }
    | {
          readonly kind: 'group';
          readonly id: string;
          readonly shiftIds: Array<string>;
          readonly shifts: Array<T>;
          readonly groupMode: 'all' | 'any';
      };

export const sortShiftsByStartTime = <T extends ShiftForOrdering>(
    shifts: ReadonlyArray<T>,
): Array<T> =>
    [...shifts].sort((left, right) => {
        if (left.shift_start_time === right.shift_start_time) {
            return left.id.localeCompare(right.id);
        }
        return left.shift_start_time.localeCompare(right.shift_start_time);
    });

export function orderShiftsForOutreach<T extends ShiftForOrdering>(
    shifts: ReadonlyArray<T>,
    shiftGroups?: ReadonlyArray<ShiftGroupForOrdering> | null,
): Array<T> {
    if (shifts.length === 0) {
        return [];
    }

    const shiftById = new Map(shifts.map((shift) => [shift.id, shift]));
    const orderedShifts: Array<T> = [];
    const seenShiftIds = new Set<string>();

    for (const group of shiftGroups ?? []) {
        const groupShiftIds = group.shiftIds ?? group.shift_ids ?? [];
        const groupedShifts = sortShiftsByStartTime(
            groupShiftIds.flatMap((shiftId) => {
                const shift = shiftById.get(shiftId);
                if (!shift || seenShiftIds.has(shiftId)) {
                    return [];
                }
                return [shift];
            }),
        );

        for (const shift of groupedShifts) {
            orderedShifts.push(shift);
            seenShiftIds.add(shift.id);
        }
    }

    orderedShifts.push(
        ...sortShiftsByStartTime(
            shifts.filter((shift) => !seenShiftIds.has(shift.id)),
        ),
    );

    return orderedShifts;
}

/**
 * Collapse the outreach's shifts + shift_groups into ordered display units.
 *
 * Each multi-shift group produces a single `group` unit whose members are
 * sorted by start time. Ungrouped shifts (and degenerate single-shift
 * groups) produce per-shift `shift` units, sorted by start time after the
 * group units.
 */
export function buildOutreachDisplayUnits<T extends ShiftForOrdering>(
    shifts: ReadonlyArray<T>,
    shiftGroups?: ReadonlyArray<ShiftGroupForDisplayUnits> | null,
): Array<OutreachDisplayUnit<T>> {
    if (shifts.length === 0) {
        return [];
    }

    const shiftById = new Map(shifts.map((shift) => [shift.id, shift]));
    const units: Array<OutreachDisplayUnit<T>> = [];
    const seenShiftIds = new Set<string>();

    for (const [groupIndex, shiftGroup] of (shiftGroups ?? []).entries()) {
        const groupShiftIds = shiftGroup.shiftIds ?? shiftGroup.shift_ids ?? [];
        const groupShifts = sortShiftsByStartTime(
            groupShiftIds.flatMap((shiftId) => {
                const shift = shiftById.get(shiftId);
                if (!shift || seenShiftIds.has(shiftId)) {
                    return [];
                }
                return [shift];
            }),
        );
        if (groupShifts.length === 0) {
            continue;
        }
        if (groupShifts.length === 1) {
            const [onlyShift] = groupShifts;
            if (!onlyShift) {
                continue;
            }
            units.push({
                kind: 'shift',
                id: onlyShift.id,
                shiftIds: [onlyShift.id],
                shifts: [onlyShift],
            });
            seenShiftIds.add(onlyShift.id);
            continue;
        }
        units.push({
            kind: 'group',
            id: shiftGroup.id ?? `group-${groupIndex + 1}`,
            shiftIds: groupShifts.map((shift) => shift.id),
            shifts: groupShifts,
            groupMode: shiftGroup.groupMode ?? shiftGroup.group_mode ?? 'all',
        });
        for (const shift of groupShifts) {
            seenShiftIds.add(shift.id);
        }
    }

    for (const shift of sortShiftsByStartTime(
        shifts.filter((shift) => !seenShiftIds.has(shift.id)),
    )) {
        units.push({
            kind: 'shift',
            id: shift.id,
            shiftIds: [shift.id],
            shifts: [shift],
        });
    }

    return units;
}
